import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { adaptEchartsOptions, SliderComponent } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import katex from 'katex';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import {
  DEFAULT_ARITHMETIC_LABELS,
  DEFAULT_GEOMETRIC_LABELS,
  DEFAULT_SEQUENCE_SERIES_PARAMS,
  DEFAULT_SLIDER_CONFIG,
  SequenceSeriesParams,
  SequenceSeriesSliderConfig,
  SequenceVariableLabels,
} from './sequence-series.model';
import { computeSequenceTerms, computeSeriesPartialSums } from './sequence-series.util';

echarts.use([
  BarChart,
  LineChart,
  SVGRenderer,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  DataZoomComponent,
]);

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

/** Round to 2 decimal places, stripping trailing zeros */
function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : parseFloat(n.toFixed(2)).toString();
}

/** Join numeric values as a sum, handling negatives: 3 + (-2) + 5 */
function joinSum(values: number[]): string {
  return values.map((v, i) => (i === 0 ? fmt(v) : v < 0 ? `+ (${fmt(v)})` : `+ ${fmt(v)}`)).join(' ');
}

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function toSubscript(n: number): string {
  return String(n)
    .split('')
    .map((ch) => (ch >= '0' && ch <= '9' ? SUBSCRIPT_DIGITS[+ch] : ch))
    .join('');
}

interface ChartColorPalette {
  base: string;
  increment: string;
  series: string;
  highlight: string;
  sumInfinity: string;
  textOnBase: string;
  barDefault: string;
  axis: string;
  grid: string;
  text: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  hoverBand: string;
  dataZoomBorder: string;
  dataZoomFill: string;
  dataZoomBg: string;
  dataZoomAccent: string;
  termHighlight: string;
}

@Component({
  selector: 'dojo-sequence-series-chart',
  standalone: true,
  imports: [NgxEchartsDirective, SliderComponent],
  templateUrl: './sequence-series.component.html',
  styleUrl: './sequence-series.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
  host: {},
})
export class SequenceSeriesComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    effect(
      () => {
        this.effectiveFirstTerm.set(this.params().firstTerm);
        this.effectiveCommonFactor.set(this.params().commonFactor);
      },
      { allowSignalWrites: true },
    );
  }

  private static readonly LIGHT_PALETTE: ChartColorPalette = {
    base: '#ec4899',
    increment: '#0ea5e9',
    series: '#8b5cf6',
    highlight: '#e6d6fe',
    sumInfinity: '#f59e0b',
    textOnBase: '#ffffff',
    barDefault: '#d1d5db',
    axis: '#b2b1ae',
    grid: '#e5e7eb',
    text: '#706f6d',
    tooltipBg: '#ffffff',
    tooltipBorder: '#d4d2cf',
    tooltipText: '#52504e',
    hoverBand: 'rgba(0, 0, 0, 0.03)',
    dataZoomBorder: '#bae1fd',
    dataZoomFill: '#e0f0fe',
    dataZoomBg: '#f0f8ff',
    dataZoomAccent: '#0ea5e9',
    termHighlight: '#9b7dd4',
  };

  /**
   * Dark-mode palette reads from global CSS variables (defined in _root-variables-colors.scss).
   * Maps each chart color to its corresponding global variable name.
   * These global variables are always present in the DOM, unlike component-scoped
   * ViewEncapsulation.None styles which are removed when the component is destroyed.
   */
  private static readonly DARK_GLOBAL_VAR_MAP: Record<keyof ChartColorPalette, string | null> = {
    base: '--pink-500',
    increment: '--sky-500',
    series: '--purple-500',
    highlight: '--purple-200',
    sumInfinity: '--amber-500',
    textOnBase: null, // always #ffffff
    barDefault: '--neutral-200',
    axis: '--neutral-300',
    grid: '--neutral-100',
    text: '--neutral-500',
    tooltipBg: '--neutral-100',
    tooltipBorder: '--neutral-300',
    tooltipText: '--neutral-900',
    hoverBand: null, // fixed rgba value
    dataZoomBorder: '--sky-200',
    dataZoomFill: '--sky-100',
    dataZoomBg: '--sky-50',
    dataZoomAccent: '--sky-500',
    termHighlight: '--purple-700',
  };

  private static readonly DARK_FIXED_OVERRIDES: Partial<ChartColorPalette> = {
    textOnBase: '#ffffff',
    hoverBand: 'rgba(169, 184, 208, 0.2)',
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly isDarkMode = toSignal(inject(ColorSchemeService).hasDarkMode$, {
    initialValue: this.document.body?.classList.contains('dark') ?? false,
  });

  private readonly chartColors = computed((): ChartColorPalette => {
    if (!this.isDarkMode()) {
      return SequenceSeriesComponent.LIGHT_PALETTE;
    }

    // Read global CSS variables from body — dark-mode variables are defined on body.dark
    // and are always present regardless of component lifecycle.
    const rootStyle = getComputedStyle(this.document.body);
    const palette = { ...SequenceSeriesComponent.LIGHT_PALETTE };

    for (const [key, varName] of Object.entries(SequenceSeriesComponent.DARK_GLOBAL_VAR_MAP)) {
      if (varName) {
        const value = rootStyle.getPropertyValue(varName).trim();
        if (value) {
          palette[key as keyof ChartColorPalette] = value;
        }
      }
    }

    return { ...palette, ...SequenceSeriesComponent.DARK_FIXED_OVERRIDES };
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private chartInstance: any = null;
  private readonly hiddenSeries = new Set<string>();
  private lastHighlightIndex = -1;

  readonly initOpts = { renderer: 'svg' } as const;

  readonly params = input<SequenceSeriesParams>(DEFAULT_SEQUENCE_SERIES_PARAMS);
  readonly sliderConfig = input<SequenceSeriesSliderConfig>(DEFAULT_SLIDER_CONFIG);
  readonly height = input<number>(400);

  readonly effectiveFirstTerm = signal(DEFAULT_SEQUENCE_SERIES_PARAMS.firstTerm);
  readonly effectiveCommonFactor = signal(DEFAULT_SEQUENCE_SERIES_PARAMS.commonFactor);

  readonly resolvedLabels = computed<Required<SequenceVariableLabels>>(() => {
    const p = this.params();
    const defaults = p.mode === 'arithmetic' ? DEFAULT_ARITHMETIC_LABELS : DEFAULT_GEOMETRIC_LABELS;
    return { ...defaults, ...(p.labels ?? {}) };
  });

  readonly terms = computed(() => {
    const p = this.params();
    return computeSequenceTerms(p.mode, this.effectiveFirstTerm(), this.effectiveCommonFactor(), p.range);
  });

  readonly partialSums = computed(() => {
    return this.params().showSeries ? computeSeriesPartialSums(this.terms()) : [];
  });

  /** KaTeX SafeHtml for each x-axis label */
  readonly xLabelHtmls = computed<SafeHtml[]>(() => {
    const p = this.params();
    const labels = this.resolvedLabels();
    const template = p.xAxisLabelTemplate;
    const result: SafeHtml[] = [];
    for (let n = p.range.from; n <= p.range.to; n++) {
      if (template) {
        result.push(this.sanitizer.bypassSecurityTrustHtml(template.replace(/\{n\}/g, String(n))));
      } else {
        result.push(this.sanitizer.bypassSecurityTrustHtml(renderLatex(`${labels.termSymbol}_{${n}}`)));
      }
    }
    return result;
  });

  /** KaTeX SafeHtml for legend items */
  readonly legendItems = computed(() => {
    const p = this.params();
    const labels = this.resolvedLabels();
    const colors = this.chartColors();
    const termName = `${labels.termSymbol}_${labels.indexSymbol}`;
    const sumName = `${labels.seriesSymbol}_${labels.indexSymbol}`;
    const items: Array<{ name: string; color: string; html: SafeHtml }> = [
      {
        name: termName,
        color: colors.barDefault,
        html: this.sanitizer.bypassSecurityTrustHtml(renderLatex(`${labels.termSymbol}_{${labels.indexSymbol}}`)),
      },
    ];
    if (p.showSeries) {
      items.push({
        name: sumName,
        color: colors.series,
        html: this.sanitizer.bypassSecurityTrustHtml(renderLatex(`${labels.seriesSymbol}_{${labels.indexSymbol}}`)),
      });

      const r = this.effectiveCommonFactor();
      if (p.mode === 'geometric' && p.showSumToInfinity && Math.abs(r) < 1) {
        const sumInf = this.effectiveFirstTerm() / (1 - r);
        const sumStr = Number.isInteger(sumInf) ? String(sumInf) : parseFloat(sumInf.toFixed(4)).toString();
        items.push({
          name: '_sumInfinity',
          color: colors.sumInfinity,
          html: this.sanitizer.bypassSecurityTrustHtml(renderLatex(`${labels.seriesSymbol}_{\\infty} = ${sumStr}`)),
        });
      }
    }
    return items;
  });

  readonly hasDataZoom = computed(() => this.terms().length > 50 || !!this.params().forceDataZoom);

  readonly gridLeft = 50;
  readonly gridRight = 20;

  readonly chartOptions = computed<EChartsOption>(() => {
    const p = this.params();
    const labels = this.resolvedLabels();
    const terms = this.terms();
    const termCount = terms.length;
    const useDataZoom = termCount > 50 || !!p.forceDataZoom;
    const useNativeXLabels = useDataZoom || !!p.xAxisLabelTemplate;
    const termName = `${labels.termSymbol}_${labels.indexSymbol}`;
    const sumName = `${labels.seriesSymbol}_${labels.indexSymbol}`;
    const colors = this.chartColors();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const series: any[] = [];
    const a = this.effectiveFirstTerm();
    const d = this.effectiveCommonFactor();

    const vPrefix = p.valuePrefix ?? '';
    const vSuffix = p.valueSuffix ?? '';
    const fmtValue = (n: number) => `${vPrefix}${fmt(n)}${vSuffix}`;

    const valueLabel = p.showValues
      ? {
          show: true,
          position: 'top' as const,
          fontSize: 10,
          color: colors.text,
          formatter: (params: { value: number }) => fmtValue(params.value),
        }
      : undefined;

    if (p.mode === 'arithmetic' && p.explainerMode) {
      const dLabel = d >= 0 ? `+${labels.commonFactorLabel}` : `${labels.commonFactorLabel}`;

      // Bars: first bar pink with "a" label, rest gray
      series.push({
        name: termName,
        type: 'bar',
        data: terms.map((val, i) => ({
          value: val,
          itemStyle: {
            color: i === 0 ? colors.base : colors.barDefault,
            borderRadius: val >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
          },
        })),
        label: p.showValues
          ? valueLabel
          : {
              show: true,
              fontSize: 11,
              fontStyle: 'italic',
              color: colors.textOnBase,
              position: 'inside' as const,
              formatter: (params: { dataIndex: number }) => (params.dataIndex === 0 ? labels.firstTermLabel : ''),
            },
        barMaxWidth: 40,
        emphasis: { disabled: true },
        // Horizontal gray connectors + vertical blue arrows
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          label: { show: false },
          data: [
            // Horizontal gray lines from each bar top to next bar's x-position
            ...terms.slice(0, -1).map((val, i) => [
              {
                xAxis: String(i),
                yAxis: val,
                lineStyle: { color: colors.axis, width: 1.5, type: 'dashed' as const },
                label: { show: false },
              },
              { xAxis: String(i + 1), yAxis: val },
            ]),
            // Vertical blue arrows showing +d
            ...terms.slice(1).map((_, i) => [
              {
                xAxis: String(i + 1),
                yAxis: terms[i],
                symbol: 'none',
                lineStyle: { color: colors.increment, width: 2, type: 'dashed' as const },
                label: {
                  show: true,
                  formatter: dLabel,
                  color: colors.increment,
                  fontStyle: 'italic',
                  fontSize: 12,
                },
              },
              {
                xAxis: String(i + 1),
                yAxis: terms[i + 1],
                symbol: 'arrow',
                symbolSize: 8,
              },
            ]),
          ],
        },
      });
    } else if (p.mode === 'geometric' && p.explainerMode) {
      const rLabel = `\u00B7${labels.commonFactorLabel}`;

      // Bars: first bar pink with "a" label, rest gray
      series.push({
        name: termName,
        type: 'bar',
        data: terms.map((val, i) => ({
          value: val,
          itemStyle: {
            color: i === 0 ? colors.base : colors.barDefault,
            borderRadius: val >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
          },
        })),
        label: p.showValues
          ? valueLabel
          : {
              show: true,
              fontSize: 11,
              fontStyle: 'italic',
              color: colors.textOnBase,
              position: 'inside' as const,
              formatter: (params: { dataIndex: number }) => (params.dataIndex === 0 ? labels.firstTermLabel : ''),
            },
        barMaxWidth: 40,
        emphasis: { disabled: true },
        // Horizontal gray connectors + vertical blue arrows showing ×r
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          label: { show: false },
          data: [
            // Horizontal gray lines from each bar top to next bar's x-position
            ...terms.slice(0, -1).map((val, i) => [
              {
                xAxis: String(i),
                yAxis: val,
                lineStyle: { color: colors.axis, width: 1.5, type: 'dashed' as const },
                label: { show: false },
              },
              { xAxis: String(i + 1), yAxis: val },
            ]),
            // Vertical blue arrows showing ×r
            ...terms.slice(1).map((_, i) => [
              {
                xAxis: String(i + 1),
                yAxis: terms[i],
                symbol: 'none',
                lineStyle: { color: colors.increment, width: 2, type: 'dashed' as const },
                label: {
                  show: true,
                  formatter: rLabel,
                  color: colors.increment,
                  fontStyle: 'italic',
                  fontSize: 12,
                },
              },
              {
                xAxis: String(i + 1),
                yAxis: terms[i + 1],
                symbol: 'arrow',
                symbolSize: 8,
              },
            ]),
          ],
        },
      });
    } else {
      series.push({
        name: termName,
        type: 'bar',
        data: terms.map((val, i) => ({
          value: val,
          itemStyle: {
            color: i === 0 ? colors.base : colors.barDefault,
            borderRadius: val >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
          },
        })),
        barMaxWidth: 40,
        emphasis: { disabled: true },
        ...(valueLabel ? { label: valueLabel } : {}),
      });
    }

    const r = this.effectiveCommonFactor();
    const converges = p.mode === 'geometric' && p.showSumToInfinity && Math.abs(r) < 1;
    const sumInfinity = converges ? a / (1 - r) : null;

    if (p.showSeries) {
      series.push({
        name: sumName,
        type: 'line',
        data: this.partialSums(),
        itemStyle: { color: colors.series },
        lineStyle: { width: 2 },
        symbol: 'circle',
        symbolSize: 6,
        ...(valueLabel ? { label: { ...valueLabel, color: colors.series } } : {}),
      });

      if (converges && sumInfinity !== null) {
        // S∞ as a separate line series so it can be toggled via legend
        series.push({
          name: '_sumInfinity',
          type: 'line',
          data: terms.map(() => sumInfinity),
          itemStyle: { color: colors.sumInfinity },
          lineStyle: { color: colors.sumInfinity, width: 2, type: 'dashed' },
          symbol: 'none',
          tooltip: { show: false },
        });
      }
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: colors.hoverBand } },
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        borderWidth: 1,
        textStyle: { color: colors.tooltipText },
        padding: [10, 14],
        extraCssText: 'box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.1); border-radius: 10px;',
        formatter: (params: unknown) => {
          const items = params as Array<{
            seriesName: string;
            value: number;
            dataIndex: number;
            color: string;
            seriesType: string;
          }>;
          const dataIndex = items[0].dataIndex;
          const idx = dataIndex + p.range.from;
          const lines: string[] = [];

          // Term value
          const termValue = terms[dataIndex];
          const nMinusOne = idx - p.range.from;
          const termHidden = this.hiddenSeries.has(termName);
          const sumHidden = this.hiddenSeries.has(sumName);
          const sumInfHidden = this.hiddenSeries.has('_sumInfinity');

          if (termHidden) {
            // skip term tooltip
          } else if (p.mode === 'arithmetic' && p.explainerMode && nMinusOne > 0) {
            const pink = colors.base;
            const blue = colors.increment;
            const uSym = labels.termSymbol;
            const dLabel = labels.commonFactorLabel;

            const dPart =
              nMinusOne === 1 ? `\\textcolor{${blue}}{${dLabel}}` : `${nMinusOne}\\textcolor{${blue}}{${dLabel}}`;

            lines.push(
              renderLatex(
                `\\begin{aligned} ${uSym}_{${idx}} &= \\textcolor{${pink}}{${uSym}_{${
                  p.range.from
                }}} + ${dPart} \\\\ &= ${fmt(termValue)} \\end{aligned}`,
              ),
            );
          } else if (p.mode === 'arithmetic' && p.explainerMode) {
            const pink = colors.base;
            lines.push(renderLatex(`\\textcolor{${pink}}{${labels.termSymbol}_{${idx}} = ${fmt(termValue)}}`));
          } else if (p.mode === 'geometric' && p.explainerMode && nMinusOne > 0) {
            const pink = colors.base;
            const blue = colors.increment;
            const uSym = labels.termSymbol;
            const rLabel = labels.commonFactorLabel;

            const rPart =
              nMinusOne === 1
                ? `\\cdot \\textcolor{${blue}}{${rLabel}}`
                : `\\cdot \\textcolor{${blue}}{${rLabel}^{${nMinusOne}}}`;

            lines.push(
              renderLatex(
                `\\begin{aligned} ${uSym}_{${idx}} &= \\textcolor{${pink}}{${uSym}_{${
                  p.range.from
                }}} ${rPart} \\\\ &= ${fmt(termValue)} \\end{aligned}`,
              ),
            );
          } else if (p.mode === 'geometric' && p.explainerMode) {
            const pink = colors.base;
            lines.push(renderLatex(`\\textcolor{${pink}}{${labels.termSymbol}_{${idx}} = ${fmt(termValue)}}`));
          } else {
            lines.push(renderLatex(`${labels.termSymbol}_{${idx}} = ${fmt(termValue)}`));
          }

          // Partial sum (if series line present, excluding connector)
          const lineItem = items.find((i) => i.seriesType === 'line' && !i.seriesName.startsWith('_'));
          if (lineItem && !sumHidden) {
            const purple = colors.series;
            const sSym = labels.seriesSymbol;
            const uSym = labels.termSymbol;
            const n = dataIndex + 1; // number of terms summed

            const hlTerm = colors.termHighlight;
            const pink = colors.base;
            const colorTerm = (termIdx: number) =>
              termIdx === p.range.from
                ? `\\textcolor{${pink}}{${uSym}_{${termIdx}}}`
                : `\\textcolor{${hlTerm}}{${uSym}_{${termIdx}}}`;

            if (n === 1) {
              lines.push(
                renderLatex(`\\textcolor{${purple}}{${sSym}_{${idx}}} = ${colorTerm(idx)} = ${fmt(lineItem.value)}`),
              );
            } else {
              // Build term symbols: u₁ + u₂ + ... + uₙ
              let termSymbols: string;
              if (n <= 4) {
                termSymbols = Array.from({ length: n }, (_, i) => colorTerm(i + p.range.from)).join(' + ');
              } else {
                termSymbols = `${colorTerm(p.range.from)} + ${colorTerm(p.range.from + 1)} + \\cdots + ${colorTerm(
                  idx,
                )}`;
              }

              // Build term values: 3 + (-2) + 5 + ...
              let termValues: string;
              if (n <= 4) {
                termValues = joinSum(terms.slice(0, n));
              } else {
                termValues = `${fmt(terms[0])} ${
                  terms[1] < 0 ? `+ (${fmt(terms[1])})` : `+ ${fmt(terms[1])}`
                } + \\cdots ${terms[dataIndex] < 0 ? `+ (${fmt(terms[dataIndex])})` : `+ ${fmt(terms[dataIndex])}`}`;
              }

              lines.push(
                renderLatex(
                  `\\begin{aligned} \\textcolor{${purple}}{${sSym}_{${idx}}} &= ${termSymbols} \\\\ &= ${termValues} \\\\ &= ${fmt(
                    lineItem.value,
                  )} \\end{aligned}`,
                ),
              );
            }
          }

          // S∞ formula when geometric series converges
          if (converges && sumInfinity !== null && !sumInfHidden) {
            const amber = colors.sumInfinity;
            const sSym = labels.seriesSymbol;
            const aLabel = labels.firstTermLabel;
            const rLabel = labels.commonFactorLabel;

            const pink = colors.base;
            const blue = colors.increment;

            lines.push(
              renderLatex(
                `\\begin{aligned} \\textcolor{${amber}}{${sSym}_{\\infty}} &= \\frac{\\textcolor{${pink}}{${aLabel}}}{1 - \\textcolor{${blue}}{${rLabel}}} = \\frac{${fmt(
                  a,
                )}}{1 - ${fmt(r)}} = ${fmt(sumInfinity)} \\end{aligned}`,
              ),
            );
          }

          return `<div style="line-height:1.6;font-size:14px;color:${colors.tooltipText}">${lines.join('<br/>')}</div>`;
        },
      },
      legend: { show: false },
      labelLayout: { hideOverlap: true },
      grid: {
        top: 20,
        bottom: useNativeXLabels ? 60 : 10,
        left: this.gridLeft,
        right: this.gridRight,
      },
      xAxis: {
        type: 'category',
        data: useNativeXLabels
          ? p.xAxisLabelTemplate
            ? terms.map((_, i) => p.xAxisLabelTemplate!.replace(/\{n\}/g, String(i + p.range.from)))
            : terms.map((_, i) => `${labels.termSymbol}${toSubscript(i + p.range.from)}`)
          : this.xLabelHtmls().map((_, i) => String(i)),
        axisTick: { alignWithLabel: true },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: colors.axis } },
        axisLabel: useNativeXLabels
          ? { show: true, fontSize: 11, color: colors.text, interval: 'auto' }
          : { show: false },
      },
      yAxis: {
        type: 'value',
        splitLine: { show: true, lineStyle: { color: colors.grid, type: 'dashed' } },
        splitNumber: 4,
        ...(vPrefix || vSuffix
          ? { axisLabel: { color: colors.text, formatter: (value: number) => fmtValue(value) } }
          : {}),
      },
      ...(useDataZoom
        ? {
            dataZoom: [
              {
                type: 'slider',
                xAxisIndex: 0,
                startValue: 0,
                endValue: Math.min(termCount - 1, 49),
                bottom: 5,
                height: 20,
                borderColor: colors.dataZoomBorder,
                fillerColor: colors.dataZoomFill,
                handleStyle: { color: colors.dataZoomBorder, borderColor: colors.dataZoomAccent },
                dataBackground: {
                  lineStyle: { color: colors.dataZoomBorder },
                  areaStyle: { color: colors.dataZoomBg },
                },
                selectedDataBackground: {
                  lineStyle: { color: colors.dataZoomAccent },
                  areaStyle: { color: colors.dataZoomFill },
                },
              },
            ],
          }
        : {}),
      series,
    };
  });

  readonly adaptedOptions = computed(() =>
    adaptEchartsOptions(this.chartOptions(), this.document, this.isDarkMode()),
  );

  readonly onFirstTermChange = (value: number): void => {
    this.effectiveFirstTerm.set(value);
  };

  readonly onCommonFactorChange = (value: number): void => {
    this.effectiveCommonFactor.set(value);
  };

  onChartInit(chart: unknown): void {
    this.chartInstance = chart;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ec = chart as any;
    ec.on('updateAxisPointer', (event: { axesInfo?: Array<{ value?: number }> }) => {
      if (!this.params().showSeries) {
        return;
      }
      const sumName = `${this.resolvedLabels().seriesSymbol}_${this.resolvedLabels().indexSymbol}`;
      if (this.hiddenSeries.has(sumName)) {
        return;
      }

      const dataIndex = event.axesInfo?.[0]?.value;
      if (dataIndex === undefined || dataIndex === null || dataIndex === this.lastHighlightIndex) {
        return;
      }
      this.lastHighlightIndex = dataIndex;
      this.updateBarHighlight(dataIndex);
    });

    ec.getZr().on('globalout', () => {
      if (this.lastHighlightIndex === -1) {
        return;
      }
      this.lastHighlightIndex = -1;
      this.updateBarHighlight(-1);
    });
  }

  onLegendClick(seriesName: string): void {
    if (!this.chartInstance) {
      return;
    }
    this.chartInstance.dispatchAction({ type: 'legendToggleSelect', name: seriesName });

    if (this.hiddenSeries.has(seriesName)) {
      this.hiddenSeries.delete(seriesName);
    } else {
      this.hiddenSeries.add(seriesName);
      // Reset bar highlights when S_n is hidden
      const sumName = `${this.resolvedLabels().seriesSymbol}_${this.resolvedLabels().indexSymbol}`;
      if (seriesName === sumName) {
        this.lastHighlightIndex = -1;
        this.updateBarHighlight(-1);
      }
    }
  }

  isSeriesHidden(seriesName: string): boolean {
    return this.hiddenSeries.has(seriesName);
  }

  private updateBarHighlight(hoveredIndex: number): void {
    if (!this.chartInstance) {
      return;
    }
    const terms = this.terms();
    const colors = this.chartColors();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ec = this.chartInstance as any;
    const barData = terms.map((val, i) => ({
      value: val,
      itemStyle: {
        color: i === 0 ? colors.base : hoveredIndex >= 0 && i <= hoveredIndex ? colors.highlight : colors.barDefault,
        borderRadius: val >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
      },
    }));

    ec.setOption({ series: [{ data: barData }] });
  }
}
